<template>
  <div v-if="isEditable" class="user-field">
    <generic-date-picker
      v-if="fieldKey === 'birthday'"
      :component="fieldComponent"
      :value="fieldValue"
      @input="emitValue"
    />
    <change-profile-image
      v-else-if="fieldKey === 'profile_picture'"
      :size="120"
    />
    <generic-radio-group
      v-else-if="fieldKey === 'gender'"
      :options="gendersList"
      :component="{
        ...fieldComponent,
        ...{
          props: {
            radioGroup: {
              row: true
            }
          }
        }
      }"
      :value="fieldValue"
      :horizontal="true"
      @input="emitValue"
    />
    <country-field
      v-else-if="fieldKey === 'country'"
      :field="fieldComponent"
      :custom-props="field.props"
      :value="fieldValue"
      class="my-3"
      @input="emitValue"
    />
    <generic-input
      v-else
      :component="fieldComponent"
      :value="fieldValue"
      @input="emitValue"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import {mapGetters} from 'vuex'
import {getFieldCustomRegexValidation} from '@/utils/auth/validation'
import GenericInput from '@/components/generic/GenericInput'
import ChangeProfileImage from '@/components/profile/ChangeProfileImage'
import GenericDatePicker from '@/components/generic/GenericDatePicker'
import GenericRadioGroup from '@/components/generic/GenericRadioGroup'
import CountryField from '@/components/auth/fields/CountryField'

export default {
  components: {
    GenericInput,
    ChangeProfileImage,
    GenericDatePicker,
    GenericRadioGroup,
    CountryField,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    fieldValue: {
      type: String,
      default: '',
    }
  },
  data() {
    return {
      ADDRESS_FIELDS: [
        'street',
        'city',
        'city_code',
        'street_number',
        'state',
        'country',
      ],
      FIELD_LABELS: {
        profile_picture: '',
        email: this.$t('editProfileScreen.emailLabel'),
        first_name: this.$t('editProfileScreen.firstNameLabel'),
        last_name: this.$t('editProfileScreen.lastNameLabel'),
        customer_id: this.$t('editProfileScreen.customerIdLabel'),
        birthday: this.$t('editProfileScreen.birthdayLabel'),
        gender: this.$t('editProfileScreen.genderLabel'),
        address: {
          street: this.$t('editProfileScreen.streetLabel'),
          city_code: this.$t('editProfileScreen.cityCodeLabel'),
          city: this.$t('editProfileScreen.cityLabel'),
          street_number: this.$t('editProfileScreen.streetNumberLabel'),
          state: this.$t('editProfileScreen.stateLabel'),
          country: this.$t('editProfileScreen.countryLabel'),
        },
      },
    }
  },
  computed: {
    ...mapGetters([
      'authSettings',
      'appSettings',
      'user',
      'isEmailAddressChangeable',
    ]),
    fieldKey() {
      return _.get(this.field, 'props.key')
    },
    isEditable() {
      switch (this.fieldKey) {
        case 'first_name':
        case 'last_name':
          return _.get(this.authSettings, 'attributes.name.editable', true)
        case 'customer_id':
          return _.get(this.authSettings, 'attributes.customer_id.editable', false)
        case 'email':
          return this.isEmailAddressChangeable
        case 'birthday':
          return _.get(this.authSettings, 'attributes.birthday.editable', false)
        case 'gender':
          return _.get(this.authSettings, 'attributes.gender.editable', false)
        case 'street':
          return _.get(this.authSettings, 'attributes.street.editable', false)
        case 'city':
          return _.get(this.authSettings, 'attributes.city.editable', false)
        case 'city_code':
          return _.get(this.authSettings, 'attributes.city_code.editable', false)
        case 'country':
          return _.get(this.authSettings, 'attributes.country.editable', false)
        case 'street_number':
          return _.get(this.authSettings, 'attributes.street_number.editable', false)
        case 'state':
          return _.get(this.authSettings, 'attributes.state.editable', false)
        default:
          return true
      }
    },
    isAddressField() {
      return this.ADDRESS_FIELDS.includes(this.fieldKey)
    },
    gendersList() {
      let list = _.get(this.authSettings, 'genders_list')

      if (!_.isArray(list) || list.length < 1) {
        list = [
          {
            title: this.$t('editProfileScreen.genderFemaleText'),
            value: 'female',
          },
          {
            title: this.$t('editProfileScreen.genderMaleText'),
            value: 'male',
          }
        ]
      }

      return list.map((gender) => {
        const {title, value} = gender

        return {
          label: title,
          value,
        }
      })
    },
    enableProfilePicture() {
      return _.get(this.appSettings, 'enable_profile_picture')
    },
    fieldLabel() {
      return this.isAddressField
        ? this.FIELD_LABELS.address[this.fieldKey]
        : this.FIELD_LABELS[this.fieldKey]
    },
    fieldComponent() {
      return {
        ...this.getAdditionalAttributes(),
        name: this.fieldKey,
        label: this.fieldLabel,
        placeholder: this.fieldLabel,
      }
    },
  },
  methods: {
    emitValue(newValue) {
      const payload = {
        key: this.fieldKey
      }

      if (this.fieldKey === 'country') {
        payload.value = newValue.value
      } else {
        payload.value = newValue
      }

      this.$emit('input', payload)
    },
    getAdditionalAttributes() {
      const key = ['first_name', 'last_name'].includes(this.fieldKey) ? 'name' : this.fieldKey
      const field = _.get(this.authSettings, `attributes.${key}`, {})

      return {
        ...field,
        regexValidation: getFieldCustomRegexValidation(field.validators)
      }
    }
  },
}
</script>

<style lang="scss" scoped></style>
